
import { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'

import copy from 'copy-to-clipboard'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ApiCaller } from '@frontend/api'
import type { ArtifactInfos, ChatMessage } from '@types'
import { DownloadDocument, Tooltip } from '@ui/components'
import { useSentry } from '@utils/frontend'
import markdown from '@wcj/markdown-to-html'

import { useArtifactContext } from './Artifact'

interface FooterProps {
	readonly onDeepSearch: (artifact: ArtifactInfos, artifactMessage: ChatMessage) => void;
}

export default function Footer({ onDeepSearch }: FooterProps) {
	const [isFullyDeepSearched, setIsFullyDeepSearched] = useState(false)
	const { artifactParams, isProcessingResponse, artifactContent } = useArtifactContext()
	const { artifact, artifactMessage } = artifactParams
	const { captureException } = useSentry()
	const isOpenedArtifact = Boolean(artifact)

	useEffect(() => {
		async function fetchContentCount() {
			const { selectedBrains } = artifactMessage

			if (!selectedBrains) {
				return
			}

			try {
				const everyBrain = await Promise.all(selectedBrains.map(async brain => ApiCaller.getBrain(brain.id)))
				const totalContentCount = everyBrain.reduce((prev, curr) => prev + curr.parsedContents, 0)

				const usedContentCount = artifactMessage.contents?.length ?? 0

				if (usedContentCount >= totalContentCount) {
					setIsFullyDeepSearched(true)
				} else {
					setIsFullyDeepSearched(false)
				}
			} catch (error) {
				captureException(error)
			}
		}

		if (artifact) {
			fetchContentCount()
		}
	}, [isOpenedArtifact, isProcessingResponse])

	async function handleCopy() {
		ApiCaller.track('Copied_to_clipboard')
		const cleanedContent = artifactContent
			?.replace(/<source.*?>/g, '')
			?.replace(/<\/source>/g, '') ?? ''

		copy(markdown(cleanedContent) as string, {
			format: 'text/html'
		})
		toast.success('Text copied to clipboard')
	}

	return (
		<div className="bg-zinc-200 flex justify-between py-1.5 px-3 border-t border-gray-100 text-xs gap-2">
			<div className="flex gap-2">
				<button
					className={ `
									flex items-center flex-shrink-0 gap-3 border rounded-md p-3 text-xs 
									transition-colors duration-300 bg-gray-100 border-gray-300 text-gray-600 
									hover:bg-gray-200
								` }
					onClick={ handleCopy }
				>
					<FontAwesomeIcon icon="clipboard" className="w-3 h-3" />
					<span className="font-bold">Copy to clipboard</span>
				</button>
				<DownloadDocument
					type="artifact"
					title={ artifact?.name ?? 'Artifact' }
					dropdownSide="up"
					// eslint-disable-next-line @typescript-eslint/naming-convention
					onDownload={ async type => ApiCaller.track(`${type}_export`, { export_source: 'Application' }) }
				/>
			</div>

			<button
				disabled={ isProcessingResponse || isFullyDeepSearched }
				data-tooltip="tooltip-deepsearch"
				className={ `flex items-center flex-shrink-0 gap-3 border rounded-md px-3 py-2 text-xs transition-colors duration-300 ${isProcessingResponse || isFullyDeepSearched
					? 'bg-gray-300 border-gray-400 text-gray-500 cursor-not-allowed'
					: 'bg-primary border-primary text-white hover:bg-white hover:border-[#5cc1ed] hover:text-[#5cc1ed]'
				}` }
				onClick={ e => {
					e.stopPropagation()
					if (!isFullyDeepSearched && artifact) {
						onDeepSearch(artifact, artifactMessage)
					}
				} }
			>
				<FontAwesomeIcon className="w-3 h-3" icon="search" />
				<span className="font-bold">Explore more documents</span>
				<Tooltip
					anchor="tooltip-deepsearch"
					defaultPlace="top"
					offset={ 10 }>
					<div>
						{ isFullyDeepSearched
							? 'All documents in the brain have been explored. There are no more documents to search through.'
							// eslint-disable-next-line max-len
							: 'VirtualBrain automatically prioritizes the most relevant documents within your brain. If you feel like the answer is not exhaustive, use this option to complete your answer by searching through more documents within the brain.' }
					</div>
				</Tooltip>
			</button>
		</div>
	)
}
